import React from 'react';
import { createReducer, createAction } from '@reduxjs/toolkit';
import { backendCall, createApiCall, eventCall } from '@reducers/utils';
import i18n from 'i18n-js';
import { Appearance } from 'react-native';

import ConnectorSetupPopup from '@components/connectorSetup/components/SetupPopup';
import { Popup, getSecureValue, isWeb } from '@helpers';
import { userLogin } from '@reducers/user';
import { goToOnboarding, saveId } from '@reducers/connector';
import { sendLog } from '@reducers/dataDogs';
import { buildOnboardingLogTitle } from '@reducers/dataDogs/helpers';
import { getTimezone } from '@reducers/membership';

import type { RootState } from '@store/types';
import type { AppDispatch } from '@reducers/utils/types';
import type { OnBoardingState, ValidateOnboardingResponse } from './types';
import type { ValidateOnboardingRequestDto, OnboardConnectorRequestDto } from '@api/generated/models';

// Re-export types for consumers
export type { OnBoardingState, ValidateOnboardingResponse } from './types';

const initState: OnBoardingState = {
  unregisteredConnector: {},
  registeredConnector: {},
  onboardingStatus: '',
  errorMessage: '',
  userProfile: {},
  code: null,
  isLoaded: false,
};

export const clear = createAction('clear');
export const saveData = createAction<Partial<OnBoardingState>>('onBoarding/saveData');
export const resetOnboardingData = createAction('onBoarding/resetOnboardingData');

export const logOnboarding = (step: string, data?: unknown) => (dispatch: AppDispatch, getState: () => RootState) => {
  const {
    onBoarding: { code },
  } = getState();
  console.log('logOnboarding', step, data);
  dispatch(sendLog({ title: buildOnboardingLogTitle(step, code ?? ''), data: { data }, status: 'debug' }));
  return false;
};

export const onFoundUnregisteredConnector =
  (data: { multipleConnectorSessionsFound?: boolean; connectorSession?: ValidateOnboardingResponse['unregisteredConnector'] }) =>
  async (dispatch: AppDispatch) => {
    dispatch(logOnboarding(`Event received onFoundUnregisteredConnector`, data));
    dispatch(
      validateOnboardingFunction({
        onboardingStatus: data.multipleConnectorSessionsFound ? 'MultipleConnectorsFound' : 'UnregisteredConnectorFound',
        registeredConnector: null,
        unregisteredConnector: {
          ...data.connectorSession,
        },
      })
    );
  };

export const onOnboardedConnector = (data: { connectorId?: string }) => async (dispatch: AppDispatch) => {
  dispatch(saveId(data.connectorId!));
  dispatch(logOnboarding(`Event received onOnboardedConnector`, data));
  Popup({
    showPopup: false,
    id: 'ConnectorSetupPopup',
  });
};

interface ValidateOnboardingFunctionData {
  code?: string | null;
  onboardingStatus?: string;
  registeredConnector?: ValidateOnboardingResponse['registeredConnector'] | null;
  unregisteredConnector?: ValidateOnboardingResponse['unregisteredConnector'] | Record<string, never>;
}

export const validateOnboardingFunction = (data: ValidateOnboardingFunctionData) => async (dispatch: AppDispatch) => {
  const code = data?.code || (data?.unregisteredConnector as { sessionKey?: string })?.sessionKey;
  if (code) {
    data.code = code;
  }
  dispatch(saveData(data as Partial<OnBoardingState>));
  const isAuthorized = getSecureValue('token');

  dispatch(logOnboarding(`parsing validateOnboarding result`, data));

  const openModal = () => {
    if (isWeb) {
      const path = window.location.pathname;
      const ignorePath = ['/link-connector', '/connect'];
      if (ignorePath.includes(path)) {
        return;
      }
    }
    Popup({
      Component: (p: Record<string, unknown>) => (
        <ConnectorSetupPopup {...{ ...p, isAuthorized, data, logOnboarding: (d: string) => dispatch(logOnboarding(d)) }} />
      ),
      width: 400,
      bg: 'transparent',
      id: 'ConnectorSetupPopup',
      centered: true,
    });
  };

  if (isAuthorized) {
    switch (data.onboardingStatus) {
      case 'UnregisteredConnectorFound':
      case 'MultipleConnectorsFound':
        return openModal();
    }
  } else {
    switch (data.onboardingStatus) {
      case 'NoConnectorFound':
        return;
      case 'UnregisteredConnectorFound':
      case 'MultipleConnectorsFound':
        return openModal();
    }
  }
};

let abortController: AbortController | null = null;

export const validateOnboarding = (code?: string | null, anonymSetup?: boolean) => async (dispatch: AppDispatch) => {
  if (code) {
    dispatch(saveData({ code, isLoaded: false }));
  } else {
    dispatch(saveData({ isLoaded: false }));
  }
  let anonym = anonymSetup;

  if (isWeb) {
    if (window.location.pathname == '/connect') {
      anonym = true;
    }
  }

  dispatch(logOnboarding(`call validateOnboarding API`, { anonym, selectedConnectorSessionKey: code }));

  if (abortController) {
    abortController.abort();
  }

  abortController = new AbortController();
  const signal = abortController.signal;

  const params: ValidateOnboardingRequestDto = {};

  if (code) {
    params.selectedConnectorSessionKey = code;
  }

  const call = createApiCall(
    (api) => (anonym ? api.membership.validateOnboardingAnonym : api.membership.validateOnboarding),
    {
      signal,
      anonym,
      params,
      onSuccess: (d) => {
        dispatch(saveData({ isLoaded: true }));
        dispatch(logOnboarding(`result validateOnboarding API`, d));

        if (!code) {
          dispatch(validateOnboardingFunction(d as ValidateOnboardingFunctionData));
        }
      },
      onError: (error: unknown) => {
        dispatch(saveData({ isLoaded: true }));
        console.log(error, 'error');
      },
    }
  );
  return await dispatch(backendCall(call));
};

interface OnboardConnectorData {
  email?: string;
  pass?: string;
  firstName?: string;
  lastName?: string;
  language?: string;
  companyName?: string;
  factoryTimeZone?: string;
  callback?: (() => void) | null;
}

export const onboardConnector =
  (data: OnboardConnectorData | null, anonym?: boolean, onError?: (message?: string) => void, updateTokenState?: (() => void) | null) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const {
      onBoarding: { code },
    } = getState();

    if (abortController) {
      abortController.abort();
    }
    if (!code) {
      return onError?.();
    }

    dispatch(saveData({ userProfile: (data as Record<string, string>) || {}, isLoaded: false }));

    // Convert timezone name to ID if data is provided
    let factoryTimeZoneId: string | null = null;
    if (data && data.factoryTimeZone) {
      const timezones = await dispatch(getTimezone());
      if (timezones) {
        const matchedTimezone = timezones.find((tz) => tz.name === data.factoryTimeZone);
        if (matchedTimezone) {
          factoryTimeZoneId = matchedTimezone.id!;
        } else {
          // Fallback: use original value if no match found
          factoryTimeZoneId = data.factoryTimeZone;
        }
      } else {
        // Fallback: use original value if timezone API fails
        factoryTimeZoneId = data.factoryTimeZone;
      }
    }

    const params: Partial<OnboardConnectorRequestDto> & { connectorSessionKey: string } = {
      connectorSessionKey: code,
    };

    if (data) {
      params.userAccount = {
        emailAddress: data.email ?? '',
        password: data.pass ?? '',
        userProfile: {
          firstName: data.firstName,
          lastName: data.lastName,
          preferedLanguage: data.language || i18n.currentLocale() || 'en',
          preferedTheme: Appearance.getColorScheme(),
        },
        companyProfile: {
          companyName: data.companyName,
          language: data.language,
          factoryTimeZone: factoryTimeZoneId,
        },
      };
    }

    dispatch(logOnboarding(`call onboardConnector API`, { anonym, params }));

    const call = createApiCall(
      (api) => (anonym ? api.membership.onboardConnectorAnonym : api.membership.onboardConnector),
      {
        anonym,
        params: params as OnboardConnectorRequestDto,
      }
    );
    try {
      const resp = (await dispatch(
        eventCall({
          connectionName: 'signalRAnonym',
          eventName: 'OnOnboardedConnector',
          eventNameError: 'OnFailedToOnboardConnector',
          data: call,
          timeout: 120,
        })
      )) as { connectorId?: string } | undefined;
      console.log(resp, 'resp');

      dispatch(logOnboarding(`result onboardConnector API`, resp));

      dispatch(goToOnboarding());

      if (resp?.connectorId) {
        dispatch(saveId(resp.connectorId));
      }

      if (anonym) {
        if (resp?.connectorId) {
          dispatch(userLogin({ ...data, callback: isWeb ? updateTokenState : null }));
        } else {
          onError?.();
        }
      } else {
        return resp;
      }
    } catch (e) {
      console.log(e, 'e');

      dispatch(logOnboarding(`error onboardConnector API`, e));

      if (onError) {
        onError((e as Error)?.message);
      }

      return e;
    }
  };

export default createReducer<OnBoardingState>(initState, {
  [saveData.type]: (state, action) => ({
    ...state,
    ...action.payload,
  }),
  [resetOnboardingData.type]: () => ({
    unregisteredConnector: {},
    registeredConnector: {},
    onboardingStatus: '',
    errorMessage: '',
    userProfile: {},
    code: null,
    isLoaded: false,
  }),
  [clear.type]: () => {
    return initState;
  },
});
